import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import {
  fetchOutputFilters,
  selectEnabledKeys,
  selectDisabledKeys,
  updateSelection,
  syncPreviousSelection,
  selectLoading,
} from '../../features/outputFilters/outputfilterSlice';
import { getOutputOverview, resetOverviewData } from '../../features/outputOverview/outputOverviewSlice';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { fetchSegmentDeepDive } from '../../features/outputSegmentDeepDive/outputSegmentDeepDiveSlice';
import { RootState } from '../../state/store';
import { fetchOutputBrands } from '../../features/outputSegmentDeepDive/outputBrandsSlice';
import { SCENARIO_OUTPUT_DATA } from '../../utils/dataConstants';
import { SelectMapperKeys, ViewByMapperKeysForDeepdive } from '../../types/common';
import { isEqual } from '../../utils/helper';
import DropdownMenu from '../filters/DropdownMenu';

interface FilterDropdownProps {
  filterKey: string;
  label: string;
  singleSelect?: boolean;
}

const OutputFilterDropdown: React.FC<FilterDropdownProps> = ({ filterKey, label, singleSelect = false }) => {
  const dispatch = useAppDispatch();

  const enabledKeys = useAppSelector((state) => selectEnabledKeys(state, filterKey));
  const disabledKeys = useAppSelector((state) => selectDisabledKeys(state, filterKey));
  const filtersState = useAppSelector((state) => state.outputFilters);
  const loading = useAppSelector(selectLoading);

  const { scenarios, selectedScenarios, isDeepdiveVisible } = useAppSelector(
    (state: RootState) => state.outputSegmentDeepDive,
  );
  const { selectedToggle, selectedViewBy } = useAppSelector((state: RootState) => state.outputFilters);

  const allKeys = [...enabledKeys, ...disabledKeys];
  const [selectedValues, setSelectedValues] = useState<string[]>(enabledKeys);
  const [isOpen, setIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(singleSelect ? false : enabledKeys.length === allKeys.length);

  useEffect(() => {
    setSelectedValues(enabledKeys);
    setSelectAll(enabledKeys.length === allKeys.length);
  }, [enabledKeys, allKeys.length]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const isChecked = event.target.checked;
    let newSelectedValues;

    if (isChecked) {
      newSelectedValues = [...selectedValues, value];
    } else {
      newSelectedValues = selectedValues.filter((item) => item !== value);
    }

    setSelectedValues(newSelectedValues);
    setSelectAll(newSelectedValues.length === allKeys.length);

    dispatch(updateSelection({ key: filterKey, value: newSelectedValues }));
  };

  const handleSelectAllToggle = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedValues(newSelectAll ? allKeys : []);

    dispatch(updateSelection({ key: filterKey, value: newSelectAll ? allKeys : [] }));
  };

  const handleClose = async () => {
    setIsOpen(false);
    if (!isEqual(filtersState.currentSelection, filtersState.previousSelection)) {
      const filterPayload = {
        scenario_names: scenarios,
        bottler: filtersState.currentSelection.bottler,
        state: filtersState.currentSelection.state,
      };
      await dispatch(showLoader());
      await dispatch(fetchOutputFilters(filterPayload));

      await dispatch(syncPreviousSelection());
      await dispatch(resetOverviewData());
      await dispatch(getOutputOverview({ scenarioNames: scenarios }));

      await dispatch(hideLoader());
      if (isDeepdiveVisible) {
        await updateDeepDiveData();
      }
    }
  };

  const updateDeepDiveData = async () => {
    await dispatch(showLoader());
    const outputBrandPayload = {
      scenario_name: scenarios,
      bottler: filtersState.currentSelection.bottler,
      state: filtersState.currentSelection.state,
    };
    const response = await dispatch(fetchOutputBrands(outputBrandPayload));
    const segmentPayload = {
      scenario_1: selectedScenarios.base,
      scenario_2: selectedScenarios.child,
      brand: response.payload,
      bottler: filtersState.currentSelection.bottler,
      state: filtersState.currentSelection.state,
      select: SCENARIO_OUTPUT_DATA.SELECT_MAPPER[selectedToggle as SelectMapperKeys],
      view_by: SCENARIO_OUTPUT_DATA.VIEW_BY_MAPPER_DEEPDIVE[selectedViewBy as ViewByMapperKeysForDeepdive],
    };
    await dispatch(fetchSegmentDeepDive(segmentPayload));
    await dispatch(hideLoader());
  };

  return (
    <DropdownMenu
      label={label}
      selectedValues={selectedValues}
      allKeys={allKeys}
      singleSelect={singleSelect}
      selectAll={selectAll}
      isOpen={isOpen}
      loading={loading}
      setIsOpen={setIsOpen}
      handleSelectAllToggle={handleSelectAllToggle}
      handleChange={handleChange}
      handleClose={handleClose}
      customStyles={{ width: '155.04px', maxWidth: '155.04px', border: `1px solid #ECECEC`, color: '#82868C' }}
    />
  );
};

export default OutputFilterDropdown;
